import { useState } from "react";
import type { AiClassification, AiDocumentSpec, AiWorkspaceOption } from "../../scenario/types";
import { Ico, type IcoName } from "./icons";
import type { FileDecision } from "./review";

/**
 * The "Review your files" window of the AI assistant (Episode 6).
 *
 * One file at a time. The learner works down the list: the file they are on is
 * selected (and is the only row that blinks), every later file is greyed out
 * until this one has a decision, and an earlier file can be re-opened to change
 * its decision. When the last file is decided the window says so, names the
 * next step ("Check your choices") and offers the button with the same words.
 * That button opens a recap of exactly what will be sent -- the account, the
 * message and every file as it will go out (black bars included) -- and only
 * its "Send" button actually sends.
 *
 * Presentational only: the parent (AiAssistant.tsx) owns all state, so the
 * scoring in `evaluate()` keeps reading the same `attached` / `redactions`.
 */

export interface FileReviewProps {
  appName: string;
  docs: AiDocumentSpec[];
  attached: string[];
  decisions: Record<string, FileDecision>;
  redactions: Record<string, Set<string>>;
  /** first undecided file (the one the learner is on), or null when all are decided. */
  currentId: string | null;
  /** file shown in the main pane (a re-opened earlier file, else `currentId`). */
  shownId: string | null;
  /** a one-click decision that is being saved (short confirmation before moving on). */
  pending: { id: string; kind: FileDecision } | null;
  view: "files" | "review";
  workspace: AiWorkspaceOption;
  prompt: string;
  onChoose: (id: string, kind: FileDecision) => void;
  onToggleLine: (id: string, segId: string) => void;
  onAttachRedacted: (id: string) => void;
  onViewDoc: (id: string) => void;
  onReopen: (id: string) => void;
  onClose: () => void;
  onCheck: () => void;
  onBackToFiles: () => void;
  onSend: () => void;
  onAsk: () => void;
  /** open / close the account menu (the files window covers the top bar). */
  onAccount: () => void;
}

const OPTIONS: { kind: FileDecision; title: string; hint: string; icon: IcoName }[] = [
  {
    kind: "yes",
    title: "Yes, send as-is",
    icon: "check",
    hint: "Use this when the file is already safe to share: it is public, or it holds only what was asked for, with no secrets.",
  },
  {
    kind: "no",
    title: "No, redact it first",
    icon: "redact",
    hint: "Use this when you need the file, but it has personal, financial or secret lines. Black those out before it goes.",
  },
  {
    kind: "skip",
    title: "Do not attach at all",
    icon: "block",
    hint: "Use this when the file is not needed for the task, or it is the full confidential original.",
  },
];

function classTone(c: AiClassification): string {
  if (c === "Public") return "cgx-class--public";
  if (c === "Internal") return "cgx-class--internal";
  if (c === "Confidential") return "cgx-class--confidential";
  if (c === "Strictly confidential") return "cgx-class--strict";
  return "cgx-class--internal";
}

function initialsOf(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** The longest leading part (ending at an underscore) that every file name shares, e.g. "NorthBay_". */
function commonPrefix(names: string[]): string {
  if (names.length < 2) return "";
  let p = names[0];
  for (const n of names) while (p && !n.startsWith(p)) p = p.slice(0, -1);
  const cut = p.lastIndexOf("_");
  return cut > 0 ? p.slice(0, cut + 1) : "";
}

/** File names have no spaces: allow a line break after each underscore so a long
 *  name wraps at a word boundary instead of in the middle of a word. */
function breakable(name: string) {
  const parts = name.split("_");
  return parts.map((p, i) => (
    <span key={i}>
      {p}
      {i < parts.length - 1 ? (
        <>
          _<wbr />
        </>
      ) : null}
    </span>
  ));
}

/** The document as a page. Editable: every line is a button that toggles a black bar. */
export function Paper({
  doc,
  hidden,
  editable,
  onToggle,
  skipped,
}: {
  doc: AiDocumentSpec;
  hidden: Set<string>;
  editable: boolean;
  onToggle?: (segId: string) => void;
  skipped?: boolean;
}) {
  return (
    <div className={`cgx-paper ${editable ? "is-editable" : ""} ${skipped ? "is-skipped" : ""}`}>
      <div className="cgx-paper-head">
        <span className={`cgx-ftype cgx-ftype--${doc.fileType}`}>{doc.fileType.toUpperCase()}</span>
        <span className="cgx-paper-name">{doc.name}</span>
        <span className={`cgx-paper-stamp ${classTone(doc.classification)}`}>{doc.classification}</span>
      </div>
      <div className="cgx-paper-body">
        {doc.segments.map((seg) => {
          const isHidden = hidden.has(seg.id);
          if (editable) {
            return (
              <button
                key={seg.id}
                type="button"
                className={`cgx-line ${isHidden ? "is-hidden" : ""}`}
                aria-pressed={isHidden}
                onClick={() => onToggle?.(seg.id)}
              >
                <span className="cgx-line-text">{seg.text}</span>
                {isHidden && <span className="cgx-line-bar" aria-hidden />}
                <span className="cgx-line-tip" aria-hidden>
                  {isHidden ? "Show again" : "Black out"}
                </span>
              </button>
            );
          }
          return (
            <p key={seg.id} className={`cgx-line is-static ${isHidden ? "is-hidden" : ""}`}>
              <span className="cgx-line-text">{seg.text}</span>
              {isHidden && <span className="cgx-line-bar" aria-hidden />}
            </p>
          );
        })}
      </div>
      {skipped && (
        <div className="cgx-paper-skip" aria-hidden>
          <Ico name="block" /> Not attached
        </div>
      )}
    </div>
  );
}

export function FileReviewPanel(p: FileReviewProps) {
  const { docs, attached, decisions, redactions } = p;
  const decided = (id: string) => attached.includes(id) || decisions[id] === "skip";
  const allDecided = docs.every((d) => decided(d.id));
  const currentIdx = p.currentId ? docs.findIndex((d) => d.id === p.currentId) : -1;
  const shown = p.shownId ? docs.find((d) => d.id === p.shownId) ?? null : null;
  const shownIdx = shown ? docs.findIndex((d) => d.id === shown.id) : -1;
  const shownDecided = shown ? decided(shown.id) : false;
  const shownDecision = shown ? decisions[shown.id] : undefined;
  const hiddenShown = shown ? redactions[shown.id] ?? new Set<string>() : new Set<string>();
  const redactMode = !!shown && !shownDecided && shownDecision === "no";
  const pendingHere = !!shown && p.pending?.id === shown.id;
  const canReopen = shownIdx >= 0 && docs.slice(0, shownIdx).every((d) => decided(d.id));
  const prefix = commonPrefix(docs.map((d) => d.name));

  const rowState = (d: AiDocumentSpec): "done" | "current" | "locked" =>
    decided(d.id) ? "done" : d.id === p.currentId ? "current" : "locked";

  const statusText = (d: AiDocumentSpec): string => {
    const st = rowState(d);
    if (st === "locked") return "Waiting";
    if (st === "current") return p.pending?.id === d.id ? "Saving" : "Deciding now";
    if (decisions[d.id] === "skip") return "Left out";
    const n = redactions[d.id]?.size ?? 0;
    return n > 0 ? `Redacted, ${n} line${n === 1 ? "" : "s"}` : "Sent as-is";
  };

  const progressText =
    p.view === "review"
      ? `${attached.length} of ${docs.length} files will be sent`
      : allDecided
      ? `All ${docs.length} files decided`
      : `File ${currentIdx + 1} of ${docs.length}`;

  return (
    <div className="cgx-panel cgx" role="dialog" aria-label={p.view === "review" ? "Check your choices" : "Review your files"}>
      <header className="cgx-panel-head">
        <div className="cgx-panel-titles">
          <span className="cgx-panel-title">
            <Ico name={p.view === "review" ? "shield" : "paperclip"} />
            {p.view === "review" ? "Check your choices" : "Review your files"}
          </span>
          <span className="cgx-panel-sub">
            {p.view === "review"
              ? "Look over what will be sent. Nothing has been sent yet."
              : "You must decide every file, one at a time. Nothing is sent yet."}
          </span>
        </div>
        <div className="cgx-panel-prog">
          <span className="cgx-dots" aria-hidden>
            {docs.map((d) => (
              <i key={d.id} className={`is-${rowState(d)}`} />
            ))}
          </span>
          <span>{progressText}</span>
        </div>
        <button type="button" className="cgx-acct-chip" aria-haspopup="menu" onClick={p.onAccount} title="Account and workspace">
          <span className={`cgx-av cgx-av--${p.workspace.kind}`} aria-hidden>
            {initialsOf(p.workspace.label)}
          </span>
          <span className="cgx-acct-chip-name">{p.workspace.label}</span>
          <Ico name="chevdown" />
        </button>
        <button type="button" className="cgx-x" aria-label="Close the files window" onClick={p.onClose}>
          <Ico name="close" />
        </button>
      </header>

      {p.view === "review" ? (
        <ReviewBody {...p} />
      ) : (
        <div className="cgx-body">
          <nav className="cgx-rail" aria-label="Files to review">
            {docs.map((d, i) => {
              const st = rowState(d);
              return (
                <button
                  key={d.id}
                  type="button"
                  title={d.name}
                  className={`cgx-rail-row is-${st} ${p.shownId === d.id ? "is-shown" : ""} ${
                    st === "current" && (redactMode || pendingHere) ? "is-quiet" : ""
                  }`}
                  disabled={st === "locked"}
                  aria-current={p.shownId === d.id ? "true" : undefined}
                  onClick={() => {
                    if (st !== "locked") p.onViewDoc(d.id);
                  }}
                >
                  <span className="cgx-rail-idx" aria-hidden>
                    {st === "done" ? <Ico name={decisions[d.id] === "skip" ? "block" : "check"} /> : st === "locked" ? <Ico name="lock" /> : i + 1}
                  </span>
                  <span className="cgx-rail-meta">
                    <span className="cgx-rail-name">{breakable(d.name.slice(prefix.length))}</span>
                    <span className="cgx-rail-status">{statusText(d)}</span>
                  </span>
                </button>
              );
            })}
          </nav>

          <section className="cgx-main" key={shown?.id ?? "all-decided"}>
            {shown ? (
              <>
                <div className="cgx-doc">
                  {shown.blurb && (
                    <p className="cgx-about">
                      <span className="cgx-about-k">About this file</span>
                      {shown.blurb}
                    </p>
                  )}
                  <Paper
                    doc={shown}
                    hidden={hiddenShown}
                    editable={redactMode}
                    skipped={shownDecided && decisions[shown.id] === "skip"}
                    onToggle={(segId) => p.onToggleLine(shown.id, segId)}
                  />
                </div>

                <div className="cgx-act">
                  {shownDecided ? (
                    <div className="cgx-saved">
                      <span className={`cgx-saved-ico ${decisions[shown.id] === "skip" ? "is-skip" : ""}`}>
                        <Ico name={decisions[shown.id] === "skip" ? "block" : "check"} />
                      </span>
                      <span className="cgx-saved-txt">
                        <strong>Your decision</strong>
                        {decisions[shown.id] === "skip"
                          ? "Left out. It will not be sent."
                          : hiddenShown.size > 0
                          ? `Redacted copy. ${hiddenShown.size} line${hiddenShown.size === 1 ? " is" : "s are"} blacked out.`
                          : "Sent as-is."}
                      </span>
                      {canReopen ? (
                        <button type="button" className="cgx-btn cgx-btn--ghost cgx-btn--sm" onClick={() => p.onReopen(shown.id)}>
                          <Ico name="edit" /> Change
                        </button>
                      ) : (
                        <span className="cgx-saved-lock">Change the earlier files first.</span>
                      )}
                    </div>
                  ) : redactMode ? (
                    <div className="cgx-redact">
                      <div className="cgx-redact-head">
                        <span className="cgx-redact-pick">
                          <Ico name="redact" /> Redact it first
                        </span>
                        <button type="button" className="cgx-link" onClick={() => p.onReopen(shown.id)}>
                          Change my choice
                        </button>
                      </div>
                      <p className="cgx-redact-help">
                        <Ico name="eyeoff" />
                        <span>
                          <strong>Click every line that must be hidden.</strong> Click it again to show it. When you are done,
                          attach the redacted copy.
                        </span>
                      </p>
                      <div className="cgx-foot">
                        <span className="cgx-foot-count">
                          <Ico name="eyeoff" />
                          {hiddenShown.size} line{hiddenShown.size === 1 ? "" : "s"} blacked out
                        </span>
                        <button
                          type="button"
                          className={`cgx-btn cgx-btn--primary cgx-btn--sm ${hiddenShown.size > 0 ? "cgx-btn--blink" : ""}`}
                          disabled={hiddenShown.size === 0}
                          onClick={() => p.onAttachRedacted(shown.id)}
                        >
                          Attach redacted copy
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="cgx-decide">
                      <p className="cgx-decide-q">
                        What should happen to this file? <em>You must decide every file.</em>
                      </p>
                      <div className="cgx-opts">
                        {OPTIONS.filter((o) => o.kind !== "no" || shown.redactable).map((o) => {
                          const picked = pendingHere && p.pending?.kind === o.kind;
                          return (
                            <button
                              key={o.kind}
                              type="button"
                              className={`cgx-opt cgx-opt--${o.kind} ${picked ? "is-picked" : ""} ${pendingHere && !picked ? "is-dim" : ""}`}
                              disabled={pendingHere}
                              onClick={() => p.onChoose(shown.id, o.kind)}
                            >
                              <span className="cgx-opt-ico">
                                <Ico name={picked ? "check" : o.icon} />
                              </span>
                              <span className="cgx-opt-body">
                                <span className="cgx-opt-t">{o.title}</span>
                                <span className="cgx-opt-h">{o.hint}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="cgx-done">
                <div className="cgx-done-head">
                  <span className="cgx-done-badge" aria-hidden>
                    <Ico name="check" />
                  </span>
                  <h3>Every file now has a decision</h3>
                </div>
                <ul className="cgx-done-list">
                  {docs.map((d) => {
                    const n = redactions[d.id]?.size ?? 0;
                    const skipped = decisions[d.id] === "skip";
                    return (
                      <li key={d.id}>
                        <span className={`cgx-ftype cgx-ftype--${d.fileType}`}>{d.fileType.toUpperCase()}</span>
                        <span className="cgx-done-name">{d.name}</span>
                        <span className={`cgx-chip ${skipped ? "cgx-chip--skip" : n > 0 ? "cgx-chip--redact" : "cgx-chip--yes"}`}>
                          {skipped ? "Left out" : n > 0 ? `Redacted, ${n} line${n === 1 ? "" : "s"}` : "Sent as-is"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <div className="cgx-done-foot">
                  <p className="cgx-done-next">
                    <strong>Next step: Check your choices.</strong> Look over what will be sent to {p.appName} before
                    anything is sent.
                  </p>
                  <button type="button" className="cgx-btn cgx-btn--primary cgx-btn--blink" onClick={p.onCheck}>
                    Check your choices
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

/** "Check your choices": everything that will go out, exactly as it will go. */
function ReviewBody(p: FileReviewProps) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const { docs, attached, redactions, decisions } = p;
  const sendCount = attached.length;
  const leftOut = docs.length - sendCount;
  const hasPrompt = p.prompt.trim().length > 0;
  const canSend = sendCount > 0 && hasPrompt;

  return (
    <div className="cgx-review">
      <div className="cgx-rv-scroll">
        <section className="cgx-rv-sec">
          <h4>Signed in as</h4>
          <div className="cgx-rv-row">
            <span className={`cgx-av cgx-av--${p.workspace.kind}`} aria-hidden>
              {initialsOf(p.workspace.label)}
            </span>
            <div className="cgx-rv-main">
              <strong>{p.workspace.label}</strong>
              <span>{p.workspace.description}</span>
            </div>
            <button type="button" className="cgx-link" onClick={p.onAccount}>
              <Ico name="edit" /> Change account
            </button>
          </div>
        </section>

        <section className="cgx-rv-sec">
          <h4>Message</h4>
          <p className="cgx-rv-msg">{hasPrompt ? p.prompt : "The message box is empty. Close this window and write a message first."}</p>
        </section>

        <section className="cgx-rv-sec">
          <h4>
            Files: {sendCount} will be sent{leftOut > 0 ? `, ${leftOut} left out` : ""}
          </h4>
          <div className="cgx-rv-files">
            {docs.map((d) => {
              const isAttached = attached.includes(d.id);
              const n = redactions[d.id]?.size ?? 0;
              const skipped = !isAttached && decisions[d.id] === "skip";
              const tone = !isAttached ? "skip" : n > 0 ? "redact" : "yes";
              const label = !isAttached ? "Left out" : n > 0 ? `Redacted, ${n} line${n === 1 ? "" : "s"}` : "Sent as-is";
              return (
                <div key={d.id} className={`cgx-rv-file is-${tone}`}>
                  <div className="cgx-rv-file-top">
                    <span className={`cgx-ftype cgx-ftype--${d.fileType}`}>{d.fileType.toUpperCase()}</span>
                    <strong className="cgx-rv-file-name">{d.name}</strong>
                    <span className={`cgx-chip cgx-chip--${tone}`}>{label}</span>
                    {isAttached && (
                      <button
                        type="button"
                        className="cgx-link"
                        onClick={() => setOpen((o) => ({ ...o, [d.id]: !o[d.id] }))}
                        aria-expanded={!!open[d.id]}
                      >
                        <Ico name={open[d.id] ? "eyeoff" : "eye"} /> {open[d.id] ? "Hide preview" : "Preview as sent"}
                      </button>
                    )}
                    <button type="button" className="cgx-link" onClick={() => p.onReopen(d.id)}>
                      <Ico name="edit" /> Change
                    </button>
                  </div>
                  {isAttached && open[d.id] && <Paper doc={d} hidden={redactions[d.id] ?? new Set<string>()} editable={false} skipped={skipped} />}
                </div>
              );
            })}
          </div>
        </section>

        {sendCount === 0 && (
          <p className="cgx-rv-warn">
            <Ico name="warn" /> No file is attached, so {p.appName} has nothing to summarise. Change a choice, or cancel
            and ask for guidance.
          </p>
        )}
        <p className="cgx-rv-note">
          Anything you leave readable in a file goes out with it. When you send, {p.appName} reads the files and drafts
          the summary. After you share it, the results compare each of your choices with the safe choice.
        </p>
      </div>
      <footer className="cgx-rv-foot">
        <button type="button" className="cgx-btn cgx-btn--ghost" onClick={p.onBackToFiles}>
          <Ico name="back" /> Back to my files
        </button>
        <button type="button" className="cgx-link cgx-link--muted" onClick={p.onAsk}>
          Cancel and ask for guidance
        </button>
        <button
          type="button"
          className={`cgx-btn cgx-btn--primary ${canSend ? "cgx-btn--blink" : ""}`}
          disabled={!canSend}
          onClick={p.onSend}
        >
          Send to {p.appName} <Ico name="send" />
        </button>
      </footer>
    </div>
  );
}
